import GeoJSON from "ol/format/GeoJSON.js";

export const IMPORT_PROVIDER = "upload";
export const IMPORT_CATEGORY_ID = "__imported_layers__";
export const IMPORT_CATEGORY_NAME = "Capas importadas";

const entries = new Map();
const geojson = new GeoJSON();
const withoutExtension = name => String(name || "").replace(/\.[^.]+$/, "");

const normalizeValue = value => {
  if (value == null || ["string", "number", "boolean"].includes(typeof value)) return value;
  if (value instanceof Date) return value.toISOString();
  try { return JSON.stringify(value); } catch (error) { return String(value); }
};

const inferValueType = value => {
  if (typeof value === "number") return "number";
  if (typeof value === "boolean") return "boolean";
  if (value instanceof Date) return "date";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) return "date";
  return "text";
};

const featureRecord = feature => {
  const properties = feature.getProperties ? feature.getProperties() : {};
  const row = {};
  Object.keys(properties).forEach(key => {
    if (key !== "geometry") row[key] = normalizeValue(properties[key]);
  });
  const geometry = feature.getGeometry ? feature.getGeometry() : null;
  row.geometry = geometry ? geojson.writeGeometryObject(geometry) : null;
  row.bbox = geometry ? geometry.getExtent().slice() : null;
  return { feature, row };
};

const inferSchema = records => {
  const schema = {};
  records.forEach(({ row }) => Object.entries(row).forEach(([field, value]) => {
    if (["geometry", "bbox"].includes(field) || value == null) return;
    const type = inferValueType(value);
    if (!schema[field]) schema[field] = type;
    else if (schema[field] !== type) schema[field] = "text";
  }));
  return schema;
};

export const importedLayerId = (fileId, datasetKey = "") => datasetKey ? `upload:${fileId}:${encodeURIComponent(datasetKey)}` : `upload:${fileId}`;

export const fileDatasets = file => {
  const datasets = file?.manifest?.datasets;
  return Array.isArray(datasets) && datasets.length ? datasets : [null];
};

export const createImportedDescriptor = (file, selected = false, dataset = null) => ({
  id: importedLayerId(file.id, fileDatasets(file).length > 1 ? dataset?.key : ""),
  fileId: file.id,
  datasetKey: dataset?.key || "",
  fileName: file.name,
  name: dataset?.name || file.display_name || withoutExtension(file.name),
  format: file?.manifest?.format || String(file.name || "").split(".").pop().toLowerCase(),
  provider: { name: IMPORT_PROVIDER },
  category_name: IMPORT_CATEGORY_NAME,
  geometryTypes: Array.isArray(dataset?.geometryTypes) ? dataset.geometryTypes.slice() : [],
  importOptions: dataset?.options || {},
  selected,
  imported: true,
});

const collectGeometryTypes = (geometry, result) => {
  if (!geometry || typeof geometry.getType !== "function") return;
  const type = geometry.getType();
  if (type === "GeometryCollection" && typeof geometry.getGeometries === "function") {
    geometry.getGeometries().forEach(child => collectGeometryTypes(child, result));
    return;
  }
  if (type) result.add(type);
};

const inferGeometryTypes = records => {
  const result = new Set();
  records.forEach(({ feature }) => collectGeometryTypes(feature?.getGeometry?.(), result));
  return Array.from(result);
};

export const importedLayerRegistry = {
  register(descriptor, layer) {
    const source = layer.getSource();
    const records = (source ? source.getFeatures() : []).map(featureRecord);
    layer.set("id", descriptor.id);
    layer.set("name", descriptor.id);
    layer.set("imported", true);
    const entry = {
      descriptor,
      layer,
      records,
      visibleRecords: records.slice(),
      metadata: {
        provider: IMPORT_PROVIDER,
        schema: inferSchema(records),
        aliases: {},
        crs: "EPSG:3857",
        geometryTypes: inferGeometryTypes(records),
      },
      cleanup: typeof layer.get("importCleanup") === "function" ? layer.get("importCleanup") : null,
    };
    Object.keys(entry.metadata.schema).forEach(field => { entry.metadata.aliases[field] = field; });
    entries.set(descriptor.id, entry);
    return entry;
  },

  get(id) { return entries.get(id) || null; },
  has(id) { return entries.has(id); },
  list() { return Array.from(entries.values()); },

  setVisibleRecords(id, records) {
    const entry = entries.get(id);
    if (!entry) return;
    entry.visibleRecords = records.slice();
    const source = entry.layer.getSource();
    source.clear();
    source.addFeatures(entry.visibleRecords.map(record => record.feature));
  },

  restore(id) {
    const entry = entries.get(id);
    if (entry) this.setVisibleRecords(id, entry.records);
  },

  remove(id) {
    const entry = entries.get(id) || null;
    if (entry && entry.cleanup) entry.cleanup();
    entries.delete(id);
    return entry;
  },

  clear() {
    entries.forEach(entry => { if (entry.cleanup) entry.cleanup(); });
    entries.clear();
  },
};
